import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Upload, 
  FileImage, 
  FileText, 
  File,
  Trash2,
  Download,
  Loader2,
  FolderOpen,
  Image,
  FileType
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const fileTypeConfig = {
  image: { icon: FileImage, color: "text-[#10B981]", bg: "bg-[#10B981]/10", label: "Image" },
  document: { icon: FileText, color: "text-[#3B82F6]", bg: "bg-[#3B82F6]/10", label: "Document" },
  logo: { icon: Image, color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10", label: "Logo" },
  autre: { icon: File, color: "text-[#64748b]", bg: "bg-[#64748b]/10", label: "Autre" },
};

export default function FilesPage() {
  const { isAdmin } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState("all");
  
  // Upload form
  const [uploadData, setUploadData] = useState({
    file: null,
    file_type: "image",
    folder: ""
  });

  useEffect(() => {
    fetchFiles();
  }, [filterType]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("file_type", filterType);
      
      const response = await axios.get(`${API}/files?${params.toString()}`);
      setFiles(response.data);
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({ ...uploadData, file });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.file) {
      toast.error("Veuillez sélectionner un fichier");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadData.file);
      formData.append("file_type", uploadData.file_type);
      if (uploadData.folder) formData.append("folder", uploadData.folder);

      await axios.post(`${API}/files/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Fichier uploadé avec succès");
      setDialogOpen(false);
      setUploadData({ file: null, file_type: "image", folder: "" });
      fetchFiles();
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm("Supprimer ce fichier ?")) return;
    
    try {
      await axios.delete(`${API}/files/${fileId}`);
      toast.success("Fichier supprimé");
      fetchFiles();
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="files-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {isAdmin ? "Fichiers Clients" : "Mes Fichiers"}
          </h1>
          <p className="text-[#94A3B8] mt-1">
            {files.length} fichier{files.length > 1 ? 's' : ''}
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0099CC] hover:bg-[#0088B5] text-white btn-glow" data-testid="upload-file-btn">
              <Upload className="mr-2 h-4 w-4" />
              Uploader un fichier
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#151B2B] border-[#2A3441]">
            <DialogHeader>
              <DialogTitle className="text-white">Uploader un fichier</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Fichier *</Label>
                <Input
                  type="file"
                  onChange={handleFileSelect}
                  className="bg-[#0B0F19] border-[#2A3441] text-white file:bg-[#0099CC] file:text-white file:border-0 file:mr-4"
                />
                {uploadData.file && (
                  <p className="text-[#94A3B8] text-sm">
                    Sélectionné: {uploadData.file.name} ({formatFileSize(uploadData.file.size)})
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Type de fichier</Label>
                <Select value={uploadData.file_type} onValueChange={(v) => setUploadData({...uploadData, file_type: v})}>
                  <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151B2B] border-[#2A3441]">
                    <SelectItem value="image">Photo / Image</SelectItem>
                    <SelectItem value="logo">Logo</SelectItem>
                    <SelectItem value="document">Document PDF/DOC</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Dossier (optionnel)</Label>
                <Input
                  value={uploadData.folder}
                  onChange={(e) => setUploadData({...uploadData, folder: e.target.value})}
                  placeholder="Ex: chantier-janvier-2026"
                  className="bg-[#0B0F19] border-[#2A3441] text-white"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-[#2A3441] text-[#94A3B8]">
                  Annuler
                </Button>
                <Button type="submit" disabled={uploading} className="flex-1 bg-[#0099CC] hover:bg-[#0088B5] text-white">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Uploader
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px] bg-[#151B2B] border-[#2A3441] text-white">
            <FileType className="h-4 w-4 mr-2 text-[#64748b]" />
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-[#151B2B] border-[#2A3441]">
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="image">Images</SelectItem>
            <SelectItem value="logo">Logos</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
            <SelectItem value="autre">Autres</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Files Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-20 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <FolderOpen className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun fichier</h3>
          <p className="text-[#94A3B8] mb-6">
            Commencez par uploader vos photos, logos ou documents
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {files.map((file) => {
            const typeConfig = fileTypeConfig[file.file_type] || fileTypeConfig.autre;
            const TypeIcon = typeConfig.icon;
            
            return (
              <div
                key={file.id}
                className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-4 card-premium"
                data-testid={`file-${file.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${typeConfig.bg}`}>
                    <TypeIcon className={`h-6 w-6 ${typeConfig.color}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{file.original_name}</h3>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs text-[#64748b]">
                      <span className={`px-2 py-0.5 rounded ${typeConfig.bg} ${typeConfig.color}`}>
                        {typeConfig.label}
                      </span>
                      <span>{formatFileSize(file.size)}</span>
                      <span>{formatDate(file.created_at)}</span>
                    </div>
                    {file.folder && (
                      <p className="text-[#64748b] text-xs mt-2">📁 {file.folder}</p>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <a 
                    href={`${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}${file.url}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button variant="outline" size="sm" className="w-full border-[#2A3441] text-[#94A3B8] hover:text-white">
                      <Download className="h-4 w-4 mr-2" />
                      Télécharger
                    </Button>
                  </a>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDelete(file.id)}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
